// Pick a chain and token for one side of the swap: chains across the top, the chain's tokens with
// balances below, a search over every chain's tokens, and any other token by address. On the
// receiving side Jupnet comes first, as a destination of its own: its tokens, each delivered
// through a route picked for the user (`jupnet-receive.ts`).

import { useMemo, useState } from "react";
import type { BalanceRpcs } from "../balances";
import { isWithdrawal, type Chain, type ChainKey, type Token } from "../chains";
import { prettyUnits, short } from "../format";
import { assetUnavailable, defaultRoute, type JupnetAsset, type RouteOption } from "../jupnet-receive";
import { tokenError } from "../validate";
import { useBalances } from "./hooks";
import { ChainLogo, IconSearch, TokenLogo, TokenOnChain } from "./icons";
import { Modal } from "./Modal";

/** `asset`: a Jupnet asset, received through `chain` (its route, picked for the user). */
export type Picked = { chain: ChainKey; token: string; delivery: "direct" | "gum_deposit"; custom?: string; asset?: string };

type Props = {
  side: "src" | "dst";
  /** the chains this side offers (src: Jupnet when the api has it); on dst Jupnet is added first */
  chains: Chain[];
  /** dst only: what can be received on Jupnet (null while `GET /jupnet` loads) */
  assets?: JupnetAsset[] | null;
  /** what is selected now; `asset`: the Jupnet asset received */
  current: { chain: ChainKey; token: string; deposit: boolean; asset?: string };
  /** the paying chain, to mark withdrawals and Jupnet routes on the receiving side */
  src?: Chain;
  owner: (c: Chain) => string | undefined;
  rpcs: BalanceRpcs;
  onPick: (p: Picked) => void;
  onClose: () => void;
};

export type JupnetRow = { asset: JupnetAsset; route?: RouteOption; unavailable: string | null; via: string };

/** The receiving side's Jupnet list: each asset with the route it would take from `src`, or why it
 *  has none, and the chains it can come through. */
export function jupnetRows(assets: JupnetAsset[], src: Chain): JupnetRow[] {
  return assets.map((a) => {
    const via = a.routes.map((r) => r.chain.label.replace(" Chain", ""));
    return { asset: a, route: defaultRoute(a, src), unavailable: assetUnavailable(a, src), via: via.length > 3 ? `${via.slice(0, 2).join(", ")} +${via.length - 2}` : via.join(", ") };
  });
}

export function TokenPicker({ side, chains, assets, current, src, owner, rpcs, onPick, onClose }: Props) {
  const [q, setQ] = useState("");
  const dst = side === "dst";
  const [view, setView] = useState<ChainKey>(dst && current.deposit ? "jupnet" : current.chain);
  const onJupnet = dst && view === "jupnet";
  const viewChain = chains.find((c) => c.key === view) ?? chains[0]!;
  const tokens = onJupnet ? [] : side === "src" ? viewChain.srcTokens : viewChain.dstTokens;
  const balances = useBalances(side === "src" && viewChain.enabled ? viewChain : null, tokens, owner(viewChain), rpcs);
  const needle = q.trim().toLowerCase();
  const rows = useMemo(() => (dst && assets && src ? jupnetRows(assets, src) : []), [dst, assets, src]);

  // searching looks through every chain on this side, and Jupnet's tokens first
  const matches = useMemo(() => {
    if (!needle) return null;
    const hit = (t: Token) => t.sym.toLowerCase().includes(needle) || (t.name ?? "").toLowerCase().includes(needle) || t.addr.toLowerCase() === needle;
    return chains.filter((c) => c.enabled).flatMap((c) => (side === "src" ? c.srcTokens : c.dstTokens).filter(hit).map((t) => ({ chain: c, token: t })));
  }, [needle, chains, side]);
  const jupnetMatches = needle ? rows.filter((r) => r.asset.sym.toLowerCase().includes(needle) || r.asset.name.toLowerCase().includes(needle)) : null;
  // any other token, by address, where the chain takes one (not Jupnet's stables, not on Jupnet)
  const customOk = !onJupnet && viewChain.family !== "jupnet" && viewChain.enabled && !!q.trim() && !tokenError(viewChain.family, q.trim()) && !matches?.length && !jupnetMatches?.length;

  const pick = (p: Picked) => {
    onPick(p);
    onClose();
  };

  const row = (c: Chain, t: Token, showChain: boolean) => {
    const selected = !current.deposit && c.key === current.chain && t.addr === current.token;
    const bal = side === "src" && c.key === viewChain.key ? balances[t.addr] : undefined;
    const tag = side === "src" && c.family !== "jupnet" && t.addr !== c.stable.addr ? "swapped to " + c.stable.sym : dst && src && isWithdrawal(src, c, t.addr) ? "withdrawal" : t.native ? "native" : null;
    return (
      <li key={`${c.key}:${t.addr}`}>
        <button className={`token-row${selected ? " selected" : ""}`} onClick={() => pick({ chain: c.key, token: t.addr, delivery: "direct" })} aria-current={selected || undefined}>
          <TokenLogo sym={t.sym} size={34} />
          <span className="token-row-main">
            <span className="token-row-sym">
              {t.sym}
              {tag && <span className="tag">{tag}</span>}
            </span>
            <span className="token-row-sub">{showChain ? c.label : (t.name ?? (t.native ? `${c.label} native` : short(t.addr, 4)))}</span>
          </span>
          {bal !== undefined && bal !== null && <span className="token-row-bal">{prettyUnits(bal, t.decimals)}</span>}
        </button>
      </li>
    );
  };

  const jupnetRow = (r: JupnetRow) => {
    const a = r.asset;
    const selected = current.deposit && current.asset === a.id;
    return (
      <li key={`jupnet:${a.id}`}>
        <button
          className={`token-row${selected ? " selected" : ""}`}
          disabled={!r.route}
          onClick={() => r.route && pick({ chain: r.route.chain.key, token: r.route.token.addr, delivery: "gum_deposit", asset: a.id })}
          aria-current={selected || undefined}
        >
          <TokenOnChain sym={a.sym} chain="jupnet" size={34} />
          <span className="token-row-main">
            <span className="token-row-sym">
              {a.sym}
              {a.unified && <span className="tag">from USDC</span>}
            </span>
            <span className="token-row-sub">{r.unavailable ?? `Jupnet · via ${r.via}`}</span>
          </span>
        </button>
      </li>
    );
  };

  return (
    <Modal title={side === "src" ? "You pay with" : "You receive"} onClose={onClose}>
      <label className="search">
        <IconSearch />
        <input data-autofocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search token or paste address" aria-label="Search token or paste address" spellCheck={false} autoComplete="off" />
      </label>

      <div className="chain-grid" role="group" aria-label="Chain">
        {dst && (
          <button
            className={`chain-chip jupnet-chip${onJupnet ? " on" : ""}`}
            disabled={!!assets && !assets.length}
            title={assets === null ? "Loading Jupnet routes…" : assets?.length ? "Receive on Jupnet" : "The api offers no route to Jupnet"}
            aria-pressed={onJupnet}
            onClick={() => setView("jupnet")}
          >
            <ChainLogo chain="jupnet" size={20} />
            <span>Jupnet</span>
          </button>
        )}
        {chains.map((c) => (
          <button
            key={c.key}
            className={`chain-chip${view === c.key && !onJupnet ? " on" : ""}`}
            disabled={!c.enabled}
            title={c.enabled ? c.label : c.disabledReason}
            aria-pressed={view === c.key && !onJupnet}
            onClick={() => setView(c.key)}
          >
            <ChainLogo chain={c.key} size={20} />
            <span>{c.label.replace(" Chain", "")}</span>
          </button>
        ))}
      </div>

      {onJupnet && !needle && <p className="picker-lede">Received in your Jupnet account. A solver delivers through a chain with gum's bank; the route is picked for you.</p>}

      <ul className="token-list" aria-label="Tokens">
        {jupnetMatches?.map(jupnetRow)}
        {matches
          ? matches.map((m) => row(m.chain, m.token, true))
          : onJupnet
            ? assets === null
              ? <li className="empty">Loading what Jupnet can receive…</li>
              : rows.map(jupnetRow)
            : tokens.map((t) => row(viewChain, t, false))}
        {customOk && (
          <li>
            <button className="token-row" onClick={() => pick({ chain: viewChain.key, token: "custom", delivery: "direct", custom: q.trim() })}>
              <TokenLogo sym="?" size={34} />
              <span className="token-row-main">
                <span className="token-row-sym">Use this token on {viewChain.label}</span>
                <span className="token-row-sub mono">{short(q.trim(), 8)}</span>
              </span>
            </button>
          </li>
        )}
        {matches && !matches.length && !jupnetMatches?.length && !customOk && (
          <li className="empty">
            No token matches “{q.trim()}”.{onJupnet ? "" : ` Paste a token address on ${viewChain.label} to use any other.`}
          </li>
        )}
      </ul>
      {side === "src" && viewChain.family === "sui" && <p className="fine">Sui pays in USDC only: the api has no swap there yet.</p>}
    </Modal>
  );
}
